import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import TemplateSelector from 'components/field/TemplateSelector';
import Form from 'components/form/Form';
import Accordion from 'components/global/Accordion';
import Authorization from 'components/global/Authorization';
import FormHeader from 'components/global/FormHeader';
import Loading from 'components/global/Loading';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import * as S from 'pages/Settings/Print/Print.styled';
import { getPrintSettings, setPrintSettings } from 'pages/Settings/Print/PrintSettingsSlice';

import { PRINT } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import PrintTemplateForm from './PrintTemplateForm';
import TemplateSelectionForm from './components/forms/TemplateSelectionForm';
import settingTypes from './static/settingTypes';

const TemplateSection = ({ setting, opened, onSubmit }) => {
	const { t } = useTranslation();

	const formMethods = useFormContext();
	const [template, setTemplate] = useState(null);
	const intialTemplateValue = useRef(null);
	const { control, resetField, setValue, getValues, watch } = formMethods;
	const printSettings = useSelector((state) => state.printSettings);

	const { isSubmitSuccessful } = useFormState({ control });

	// Wathchers
	const watchedPropJSON = watch(setting.formFieldName);

	// Helpers
	const handleTemplateChange = (template) => {
		setValue(`${setting.formFieldName}.templateName`, template.name, {
			shouldValidate: true,
			shouldDirty: true
		});
		setTemplate(template);
	};

	const handleReset = useCallback(() => {
		setTemplate(intialTemplateValue.current);
		resetField(`${setting.formFieldName}.templateName`);
	}, [intialTemplateValue]);

	// Effects
	useEffect(() => {
		const templateNameFromForm = getValues(`${setting.formFieldName}.templateName`);
		const currentTemplate =
			setting.templates.find((template) => template.name === templateNameFromForm) ?? 'template1';
		setTemplate(currentTemplate);
		intialTemplateValue.current = currentTemplate;
	}, []);

	useEffect(() => {
		if (isSubmitSuccessful) {
			intialTemplateValue.current = template;
		}
	}, [isSubmitSuccessful]);

	useEffect(() => {
		if (printSettings && printSettings[setting.formFieldName]) {
			const propJSON = printSettings[setting.formFieldName];
			setTemplate(setting.templates.find((template) => template.name === propJSON.templateName));
		}
	}, [printSettings]);

	return (
		<Authorization path={setting.roleName}>
			<Accordion
				header={setting.label}
				headerTooltip={setting.description}
				style={{ width: '100%' }}
				defaultVisible={opened}>
				<div>
					<S.AccordionInner>
						<S.TemplateArea>
							{template && (
								<TemplateSelector
									name={template.name}
									imageUrl={t(template.image)}
									className="selected"
								/>
							)}
						</S.TemplateArea>
						<S.ButtonSection>
							<Button
								variant="outline"
								label="print.changeTemplate"
								size="small"
								key="print.changeTemplate"
								onClick={() =>
									NiceModal.show(TemplateSelectionForm, {
										setting,
										templateName: template.name,
										formMethods,
										handleReset,
										onSubmit,
										handleTemplateChange
									})
								}
							/>
							<Button
								variant="blue"
								label="print.editSettings"
								size="small"
								key="print.editSettings"
								onClick={() => {
									NiceModal.show(PrintTemplateForm, {
										formMethods,
										fieldName: setting.formFieldName,
										templateName: template?.name,
										templateType: setting.label,
										template,
										propJSON: watchedPropJSON,
										onSubmit
									});
								}}
							/>
						</S.ButtonSection>
					</S.AccordionInner>
				</div>
			</Accordion>
		</Authorization>
	);
};

const Print = () => {
	const axios = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const [isLoading, setIsLoading] = useState(true);
	const dispatch = useDispatch();
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					invoicePrintPropJSON: yup.mixed(),
					branchPrintPropJSON: yup.mixed(),
					awbPrintPropJSON: yup.mixed()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: async () => await loadPrintSettings()
	});

	const { reset, handleSubmit } = formMethods;

	const loadPrintSettings = useCallback(async () => {
		setIsLoading(true);

		try {
			const printSettings = await dispatch(getPrintSettings()).unwrap();
			const resetData = {
				invoicePrintPropJSON: printSettings.invoicePrintPropJSON,
				branchPrintPropJSON: printSettings.branchPrintPropJSON,
				awbPrintPropJSON: printSettings.awbPrintPropJSON
			};
			return resetData;
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	}, []);

	const onSubmit = (data) => {
		const newData = {
			invoicePrintPropJSON: JSON.stringify(data.invoicePrintPropJSON),
			branchPrintPropJSON: JSON.stringify(data.branchPrintPropJSON),
			awbPrintPropJSON: JSON.stringify(data.awbPrintPropJSON)
		};

		axios
			.post(PRINT.UPDATE_PRINT_SETTINGS, {
				...newData
			})
			.then((response) => {
				if (response.status === 200) {
					dispatch(setPrintSettings(data));
					reset({ ...data }, { shouldDirty: false });
					showSuccess();
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return !isLoading ? (
		<div>
			<HeaderContainerFormHeader>
				<FormHeader boldTitle="printSettings" />
			</HeaderContainerFormHeader>
			<Form
				formMethods={formMethods}
				style={{
					backgroundColor: 'transparent',
					border: 'none',
					padding: '0',
					overflowX: 'hidden',
					gap: 'var(--padding-3)'
				}}>
				{settingTypes.map((setting, index) => (
					<TemplateSection
						key={index}
						setting={setting}
						opened={index === 0}
						onSubmit={() => {
							handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})();
						}}
					/>
				))}
			</Form>
		</div>
	) : (
		<Loading />
	);
};

export default Print;
